// src/utils/cacheService.js

/**
 * Simple in-memory cache implementation
 * Helps reduce API calls and improves performance
 */
class CacheService {
  constructor(cacheDuration = 30 * 60 * 1000) {
    // Default 30 minutes
    this.cache = {};
    this.cacheDuration = cacheDuration;
  }

  /**
   * Get item from cache if available and not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if not found/expired
   */
  get(key) {
    const item = this.cache[key];

    if (!item) return null;

    // Check if cache is expired
    if (Date.now() > item.expiry) {
      delete this.cache[key];
      return null;
    }

    return item.data;
  }

  /**
   * Set item in cache with expiration
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} [customDuration] - Custom cache duration in ms
   */
  set(key, data, customDuration) {
    const expiry = Date.now() + (customDuration || this.cacheDuration);
    this.cache[key] = { data, expiry };
  }

  /**
   * Remove item from cache
   * @param {string} key - Cache key
   */
  remove(key) {
    delete this.cache[key];
  }

  /**
   * Clear all items from cache
   */
  clear() {
    this.cache = {};
  }

  /**
   * Get number of items in cache
   * @returns {number} Number of cached items
   */
  size() {
    return Object.keys(this.cache).length;
  }
}

// Export a singleton instance
export const apiCache = new CacheService();
